// utils/availabilityHelpers.js
import { addDays, differenceInDays } from 'date-fns';

/**
 * Vérifie si une date dépasse l'horizon de réservation
 * @param {Date} date - La date à vérifier
 * @param {Object} horizonSettings - Paramètres d'horizon {maxPublicBookableDate, maxBookingHorizonMonths, enabled}
 * @param {boolean} adminMode - Si true, l'horizon n'est pas appliqué
 * @returns {boolean} - true si la date dépasse l'horizon
 */


// ✅ NOUVEAU CODE :
export const isDateBeyondHorizon = (date, horizonSettings, adminMode = false, memberMode = false) => {
  // Admin n'est jamais bloqué
  if (adminMode) return false;
  
  // Si pas de paramètres d'horizon
  if (!horizonSettings) return false;
  
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  
  // Choisir la bonne date limite selon le mode
  const maxDateField = memberMode ? 'maxMemberBookableDate' : 'maxPublicBookableDate';
  
  // Vérifier la date limite (fixe)
  if (horizonSettings[maxDateField]) {
    const maxDate = new Date(horizonSettings[maxDateField]);
    maxDate.setHours(0, 0, 0, 0);
    if (checkDate > maxDate) return true;
  }
  
  // Vérifier maxBookingHorizonMonths (horizon relatif en mois)
  if (horizonSettings.enabled && horizonSettings.maxBookingHorizonMonths) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxHorizonDate = new Date(today);
    maxHorizonDate.setMonth(maxHorizonDate.getMonth() + horizonSettings.maxBookingHorizonMonths);
    if (checkDate > maxHorizonDate) return true;
  }
  
  return false;
};



/**
 * Obtient le statut d'un jour donné
 * @param {Date} date - La date à vérifier
 * @param {string} roomTypeId - L'ID du type de chambre (optionnel)
 * @param {Array} blockedDates - Les dates bloquées
 * @param {Array} rooms - Les chambres
 * @param {Array} availabilityRules - Les règles de disponibilité
 * @param {Object} defaultRules - Les règles par défaut
 * @param {boolean} memberMode - Si true, les périodes avec accessibleToMembers sont ouvertes
 * @param {Object} horizonSettings - Paramètres d'horizon de réservation
 * @param {boolean} adminMode - Si true, l'horizon n'est pas appliqué
 */
export const getDayStatus = (date, roomTypeId, blockedDates, rooms, availabilityRules, defaultRules, memberMode = false, horizonSettings = null, adminMode = false) => {
  // ✅ S'assurer que la date est normalisée comme date pure (sans heure/timezone)
  const checkDate = typeof date === 'string' 
    ? new Date(date + 'T00:00:00') 
    : new Date(date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0') + 'T00:00:00');
  
  // PRIORITÉ 0 : Vérifier l'horizon de réservation (sauf admin)
  if (isDateBeyondHorizon(checkDate, horizonSettings, adminMode, memberMode)) {
    return { 
      isOpen: false, 
      canArrive: false, 
      canDepart: false, 
      canStay: false,
      isBeyondHorizon: true,
      customMessage: 'Dates non encore ouvertes à la réservation'
    };
  }
  
  // PRIORITÉ 1 : Vérifier si la date est bloquée
  const isBlocked = blockedDates.some(blocked => {
    const blockedDate = new Date(blocked.date);
    blockedDate.setHours(0,0,0,0);
    const compareDateCheck = new Date(checkDate);
    compareDateCheck.setHours(0,0,0,0);
    
    const dateMatch = blockedDate.getTime() === compareDateCheck.getTime();
    const typeMatch = !blocked.roomTypes || blocked.roomTypes.length === 0 || 
                     (roomTypeId && blocked.roomTypes.includes(roomTypeId));
    
    return dateMatch && typeMatch;
  });
  
  if (isBlocked) {
    return { 
      isOpen: false, 
      canArrive: false, 
      canDepart: false, 
      canStay: false,
      isBlocked: true,
      customMessage: ''
    };
  }
  
  // ✅ FILTRER uniquement les règles ACTIVES
  const activeRules = availabilityRules.filter(rule => rule.active !== false);
  
  // PRIORITÉ 2 : Chercher les règles spécifiques applicables
  const possibleStatuses = new Set();
  const possibleRules = [];
  
  for (const rule of activeRules) {
    // Filtrer selon appliedTo et memberMode
    const appliedTo = rule.appliedTo || 'all';
    
    // Si la règle est pour le public uniquement ET qu'on est en mode membre → ignorer
    if (appliedTo === 'public' && memberMode) {
      continue;
    }
    
    // Si la règle est pour les membres uniquement ET qu'on est en mode public → ignorer
    if (appliedTo === 'members' && !memberMode) {
      continue;
    }
    
    const ruleStart = new Date(rule.startDate);
    const ruleEnd = new Date(rule.endDate);
    ruleStart.setHours(0,0,0,0);
    ruleEnd.setHours(0,0,0,0);
    const compareDateCheck = new Date(checkDate);
    compareDateCheck.setHours(0,0,0,0);
    
    if (compareDateCheck >= ruleStart && compareDateCheck <= ruleEnd) {
      // Si pas de roomTypeId ou si la règle s'applique à ce type
      const typeMatch = !rule.roomTypes || rule.roomTypes.length === 0 ||
                       (roomTypeId && rule.roomTypes.includes(roomTypeId)) ||
                       !roomTypeId;
      
      if (typeMatch) {
        const dayOfWeek = checkDate.getDay();
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayConfig = rule.weekConfig?.[dayNames[dayOfWeek]];
        
        if (dayConfig) {
          let normalizedConfig = dayConfig;
          if (dayConfig === 'in') normalizedConfig = 'in_only';
          else if (dayConfig === 'out') normalizedConfig = 'out_only';
          else if (dayConfig === 'sejour') normalizedConfig = 'sejour_only';
          
          possibleStatuses.add(normalizedConfig);
          possibleRules.push({
            config: normalizedConfig,
            minNights: rule.minNights,
            maxNights: rule.maxNights,
            priority: rule.priority,
            customMessage: rule.customMessage || null,
            appliedTo: rule.appliedTo || 'all'
          });
        }
      }
    }
  }
  
  // Si on a trouvé des règles spécifiques, les appliquer
  if (possibleStatuses.size > 0) {
    // Appliquer les règles de plus haute priorité
    const rulesByPriority = {};
    for (const rule of possibleRules) {
      if (!rulesByPriority[rule.priority]) {
        rulesByPriority[rule.priority] = [];
      }
      rulesByPriority[rule.priority].push(rule);
    }
    
    const highestPriority = Math.max(...Object.keys(rulesByPriority).map(Number));
    const highPriorityRules = rulesByPriority[highestPriority];
    
    let canArrive = false;
    let canDepart = false;
    let canStay = false;
    let isOpen = false;
    let customMessage = null;
    
    for (const rule of highPriorityRules) {
      const config = rule.config;
      
      if (rule.customMessage) {
        customMessage = rule.customMessage;
      }
      
      if (config === 'flexible') {
        canArrive = true;
        canDepart = true;
        canStay = true;
        isOpen = true;
      } else if (config === 'in_only') {
        canArrive = true;
        canStay = true;
        isOpen = true;
      } else if (config === 'out_only') {
        canDepart = true;
        canStay = true;
        isOpen = true;
      } else if (config === 'sejour_only') {
        canStay = true;
        isOpen = true;
      } else if (config === 'ferme') {
        // Gestion du statut 'ferme' dans les règles
        // Ne rien activer, tout reste false
      }
    }
    
    let minNights = 1;
    let maxNights = 30;
    const isMembersOnly = highPriorityRules.some(r => r.appliedTo === 'members');
    
    for (const rule of highPriorityRules) {
      minNights = Math.max(minNights, rule.minNights);
      maxNights = Math.min(maxNights, rule.maxNights);
    }
    
    const result = { 
      isOpen, 
      canArrive, 
      canDepart, 
      canStay,
      minNights,
      maxNights,
      statuses: highPriorityRules.map(r => r.config),
      customMessage: customMessage,
      isMembersOnly // NOUVEAU : pour les hachures jaunes dans CalendarView
    };
    
    return result;
  }
  
  // PRIORITÉ 3 : Si pas de règles spécifiques, utiliser les règles par défaut
  const dayOfWeek = checkDate.getDay();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  const defaultConfig = defaultRules.weekConfig[dayNames[dayOfWeek]] || 'flexible';
  
  let isOpen = false;
  let canArrive = false;
  let canDepart = false;
  let canStay = false;
  
  if (defaultConfig === 'flexible') {
    isOpen = true;
    canArrive = true;
    canDepart = true;
    canStay = true;
  } else if (defaultConfig === 'in_only' || defaultConfig === 'in') {
    isOpen = true;
    canArrive = true;
    canStay = true;
  } else if (defaultConfig === 'out_only' || defaultConfig === 'out') {
    isOpen = true;
    canDepart = true;
    canStay = true;
  } else if (defaultConfig === 'sejour_only' || defaultConfig === 'sejour') {
    isOpen = true;
    canStay = true;
  } else if (defaultConfig === 'ferme') {
    // Gestion du statut 'ferme'
    isOpen = false;
    canArrive = false;
    canDepart = false;
    canStay = false;
  }
  
  const result = { 
    isOpen,
    canArrive,
    canDepart,
    canStay,
    minNights: defaultRules.minNights,
    maxNights: defaultRules.maxNights,
    customMessage: defaultRules.customMessage
  };
  
  return result;
};

/**
 * Obtient les règles applicables pour une réservation
 */
export const getApplicableRules = (checkIn, checkOut, roomTypeId, availabilityRules, memberMode = false) => {
  // ✅ FILTRER uniquement les règles ACTIVES
  const activeRules = availabilityRules.filter(rule => rule.active !== false);
  
  const nights = differenceInDays(checkOut, checkIn);
  const applicableRules = [];

  for (const rule of activeRules) {
    // Filtrer selon appliedTo et memberMode
    const appliedTo = rule.appliedTo || 'all';
    
    // Si la règle est pour le public uniquement ET qu'on est en mode membre → ignorer
    if (appliedTo === 'public' && memberMode) {
      continue;
    }
    
    // Si la règle est pour les membres uniquement ET qu'on est en mode public → ignorer
    if (appliedTo === 'members' && !memberMode) {
      continue;
    }
    
    if (roomTypeId && rule.roomTypes && rule.roomTypes.length > 0) {
      if (!rule.roomTypes.includes(roomTypeId)) continue;
    }

    const ruleStart = new Date(rule.startDate);
    const ruleEnd = new Date(rule.endDate);
    ruleStart.setHours(0,0,0,0);
    ruleEnd.setHours(0,0,0,0);
    
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    checkInDate.setHours(0,0,0,0);
    checkOutDate.setHours(0,0,0,0);
    
    if (checkInDate >= ruleStart && checkOutDate <= addDays(ruleEnd, 1)) {
      if (nights >= rule.minNights && nights <= rule.maxNights) {
        applicableRules.push(rule);
      }
    }
  }

  return applicableRules;
};

/**
 * Vérifie si un pattern de réservation est valide
 */
export const isValidBookingPattern = (checkIn, checkOut, roomTypeId, blockedDates, availabilityRules, getApplicableRulesFunc, defaultRules, memberMode = false) => {
  if (!checkIn || !checkOut) return false;
  
  const nights = differenceInDays(checkOut, checkIn);
  
  if (nights < 1) return false;
  
  // Vérifier les dates bloquées
  let currentDate = new Date(checkIn);
  while (currentDate < checkOut) {
    const isBlocked = blockedDates.some(blocked => {
      const blockedDate = new Date(blocked.date);
      blockedDate.setHours(0,0,0,0);
      currentDate.setHours(0,0,0,0);
      
      const dateMatch = blockedDate.getTime() === currentDate.getTime();
      const typeMatch = !blocked.roomTypes || blocked.roomTypes.length === 0 || 
                       (roomTypeId && blocked.roomTypes.includes(roomTypeId));
      
      return dateMatch && typeMatch;
    });
    
    if (isBlocked) {
      return false;
    }
    currentDate = addDays(currentDate, 1);
  }

  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  checkInDate.setHours(0,0,0,0);
  checkOutDate.setHours(0,0,0,0);
  
  // ✅ FILTRER uniquement les règles ACTIVES
  const activeRules = availabilityRules.filter(rule => rule.active !== false);
  
  // Filtrer les règles selon appliedTo et memberMode
  const effectiveRules = activeRules.filter(r => {
    const appliedTo = r.appliedTo || 'all';
    
    // Si règle pour public uniquement ET mode membre → exclure
    if (appliedTo === 'public' && memberMode) return false;
    
    // Si règle pour membres uniquement ET mode public → exclure
    if (appliedTo === 'members' && !memberMode) return false;
    
    return true;
  });
  
  // Vérifier s'il y a des règles pour cette période
  let hasRulesForPeriod = false;
  for (const rule of effectiveRules) {
    const ruleStart = new Date(rule.startDate);
    const ruleEnd = new Date(rule.endDate);
    ruleStart.setHours(0,0,0,0);
    ruleEnd.setHours(0,0,0,0);
    
    if (checkInDate <= ruleEnd && checkOutDate > ruleStart) {
      const typeMatch = !rule.roomTypes || rule.roomTypes.length === 0 || 
                       (roomTypeId && rule.roomTypes.includes(roomTypeId)) 
                       //||
                       //!roomTypeId;
      if (typeMatch) {
        hasRulesForPeriod = true;
        break;
      }
    }
  }
  
  // Si pas de règles pour cette période, on utilise les règles par défaut
  if (!hasRulesForPeriod) {
    const valid = nights >= defaultRules.minNights && nights <= defaultRules.maxNights;
    return valid;
  }

  // Utiliser getApplicableRulesFunc qui a été passée comme fonction
  const applicableRules = getApplicableRulesFunc(checkIn, checkOut, roomTypeId, effectiveRules);
  
  if (applicableRules.length === 0) {
    return false;
  }

  // Vérifier les configurations des jours
  for (const rule of applicableRules) {
    let isValid = true;
    
    const checkInDay = checkIn.getDay();
    const checkOutDay = checkOut.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    const checkInConfig = rule.weekConfig?.[dayNames[checkInDay]];
    const checkOutConfig = rule.weekConfig?.[dayNames[checkOutDay]];
    
    const normalizeConfig = (config) => {
      if (config === 'in') return 'in_only';
      if (config === 'out') return 'out_only';
      if (config === 'sejour') return 'sejour_only';
      return config;
    };
    
    const checkInNorm = normalizeConfig(checkInConfig);
    const checkOutNorm = normalizeConfig(checkOutConfig);
    
    if (checkInNorm !== 'flexible' && checkInNorm !== 'in_only') {
      isValid = false;
      continue;
    }
    
    if (checkOutNorm !== 'flexible' && checkOutNorm !== 'out_only') {
      isValid = false;
      continue;
    }
    
    // Vérifier les jours intermédiaires
    currentDate = addDays(checkIn, 1);
    while (currentDate < checkOut) {
      const dayOfWeek = currentDate.getDay();
      const dayConfig = normalizeConfig(rule.weekConfig?.[dayNames[dayOfWeek]]);
      
      if (dayConfig === 'ferme') {
        isValid = false;
        break;
      }
      currentDate = addDays(currentDate, 1);
    }
    
    if (isValid) {
      return true;
    }
  }
  
  return false;
};

/**
 * Obtient les durées possibles à partir d'une date
 */
export const getPossibleDurationsFromDate = (checkInDate, selectedRoomType, availabilityRules, defaultRules, memberMode = false, rooms = [], reservations = []) => {
  const possibleDurations = new Set();
  const checkIn = new Date(checkInDate);
  checkIn.setHours(0,0,0,0);

  // ✅ FILTRER uniquement les règles ACTIVES
  const activeRules = availabilityRules.filter(rule => rule.active !== false);

  // Filtrer les règles selon appliedTo et memberMode
  const effectiveRules = activeRules.filter(r => {
    const appliedTo = r.appliedTo || 'all';
    if (appliedTo === 'public' && memberMode) return false;
    if (appliedTo === 'members' && !memberMode) return false;
    return true;
  });

  // Vérifie si au moins une chambre parmi roomIds est libre à la date checkIn
  const hasAvailableRoomInIds = (roomIds) => {
    if (!roomIds || roomIds.length === 0) return true;
    return roomIds.some(roomId => {
      return !reservations.some(res => {
        if (res.roomId !== roomId && !(res.roomIds || []).includes(roomId)) return false;
        if (res.status === 'cancelled') return false;
        const resIn = new Date(res.checkIn || res.startDate);
        const resOut = new Date(res.checkOut || res.endDate);
        resIn.setHours(0,0,0,0);
        resOut.setHours(0,0,0,0);
        return checkIn >= resIn && checkIn < resOut;
      });
    });
  };

  // Collecter les règles applicables avec leur priorité
  const matchingRules = [];

  for (const rule of effectiveRules) {
    const ruleStart = new Date(rule.startDate);
    const ruleEnd = new Date(rule.endDate);
    ruleStart.setHours(0,0,0,0);
    ruleEnd.setHours(0,0,0,0);

    if (checkIn >= ruleStart && checkIn <= ruleEnd) {
      const typeMatch = !rule.roomTypes || rule.roomTypes.length === 0 ||
                     (selectedRoomType && rule.roomTypes.includes(selectedRoomType)) ||
                     !selectedRoomType;

      const roomIdsMatch = !rule.roomIds || rule.roomIds.length === 0 ||
                           hasAvailableRoomInIds(rule.roomIds);

      if (typeMatch && roomIdsMatch) {
        matchingRules.push(rule);
      }
    }
  }

  if (matchingRules.length === 0) {
    for (let n = defaultRules.minNights; n <= defaultRules.maxNights; n++) {
      possibleDurations.add(n);
    }
  } else {
    // minNights = règle de la priorité la plus haute
    const highestPriority = Math.max(...matchingRules.map(r => r.priority || 1));
    const topRules = matchingRules.filter(r => (r.priority || 1) === highestPriority);
    const minNights = Math.min(...topRules.map(r => r.minNights));

    // maxNights = maximum parmi toutes les règles applicables (toutes priorités)
    const maxNights = Math.max(...matchingRules.map(r => r.maxNights));

    for (let n = minNights; n <= maxNights; n++) {
      possibleDurations.add(n);
    }
  }
  
  const result = Array.from(possibleDurations).sort((a, b) => a - b);
  return result;
};